var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import styled from 'styled-components';
import { Alert, AlertTitle, Card, Box, Stack, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OrderPriceComponent from '../organisms/OrderPrice';
import { formatMoneyAmount } from '../../utils/format';
import TypicaHandlingRateExplanation from '../molecules/TypicaHandlingRateExplanation';
import { TypicaStockHandlingRates } from '../../constants/orderValues';
var OrderPriceTable = styled(OrderPriceComponent)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  width: 100%;\n  margin-top: 16px;\n"], ["\n  width: 100%;\n  margin-top: 16px;\n"])));
var buildOrderPrice = function (offers, simulatedPrice) {
    var items = simulatedPrice.items.map(function (item) {
        var offerId = item.offerId, remainingProps = __rest(item, ["offerId"]);
        var offer = offers.find(function (offer) { return offer.id === offerId; });
        if (!offer)
            throw Error('buildInvoice offer not found');
        return __assign({ offerId: offer.id, offerLotNumber: offer.lotNumber }, remainingProps);
    });
    var total = simulatedPrice.items
        .map(function (item) { return parseFloat(item.totalFee); })
        .reduce(function (sum, current) { return sum + current; }, 0);
    return {
        total: total,
        items: items,
        taxName: simulatedPrice.taxName,
        currency: simulatedPrice.currency,
        isJpCalculation: simulatedPrice.isJpCalculation,
    };
};
var calcHandingFee = function (typicaHandlingFeeCalculation, totalBagAmount, totalWeight) {
    var type = typicaHandlingFeeCalculation.type, rates = typicaHandlingFeeCalculation.rates;
    if (type === 'bagAmount') {
        var rate_1 = rates;
        if (totalBagAmount < 5) {
            return Number(rate_1['less_than_5_bags']);
        }
        else if (totalBagAmount < 10) {
            return Number(rate_1['more_than_5_bags']);
        }
        else if (totalBagAmount < 50) {
            return Number(rate_1['more_than_10_bags']);
        }
        else {
            return Number(rate_1['more_than_50_bags']);
        }
    }
    var rate = rates;
    if (totalWeight < 300) {
        return Number(rate['less_than_300kg']);
    }
    else if (totalWeight < 600) {
        return Number(rate['more_than_300kg']);
    }
    else if (totalWeight < 3000) {
        return Number(rate['more_than_600kg']);
    }
    else {
        return Number(rate['more_than_3000kg']);
    }
};
var SimulatedOrderPrice = function (_a) {
    var itemType = _a.itemType, offers = _a.offers, simulatedOrderPrice = _a.simulatedOrderPrice, renderDiscountFields = _a.renderDiscountFields, _b = _a.isUSRoaster, isUSRoaster = _b === void 0 ? false : _b;
    var t = useTranslation().t;
    var orderPrice = buildOrderPrice(offers, simulatedOrderPrice);
    var offerList = offers[0].offerList;
    var totalBagAmount = simulatedOrderPrice.items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    var totalWeight = simulatedOrderPrice.items.reduce(function (sum, item) { return sum + parseFloat(item.weight) * item.quantity; }, 0);
    var orderedTotalBagAmount = React.useMemo(function () { return simulatedOrderPrice.ordersTotalQuantity - totalBagAmount; }, [totalBagAmount, simulatedOrderPrice]);
    var orderedTotalWeight = React.useMemo(function () { return parseFloat(simulatedOrderPrice.ordersTotalWeight) - totalWeight; }, [totalWeight, simulatedOrderPrice]);
    var orderedOneTimeUpfrontTotalBagAmount = React.useMemo(function () {
        if (simulatedOrderPrice.paymentTiming === 'one_time_upfront') {
            return (simulatedOrderPrice.oneTimeUpfrontOrdersTotalQuantity - totalBagAmount);
        }
        return simulatedOrderPrice.oneTimeUpfrontOrdersTotalQuantity;
    }, [simulatedOrderPrice, totalBagAmount]);
    var typicaHandlingRatesAverage = React.useMemo(function () {
        var offerTypicaHandlingRates = simulatedOrderPrice.items
            .map(function (item) {
            var offer = offers.find(function (offer) { return offer.id === item.offerId; });
            return offer === null || offer === void 0 ? void 0 : offer.typicaHandlingRates;
        })
            .filter(function (rate) { return rate; });
        return calculateAverageRate(offerTypicaHandlingRates.map(function (rates) {
            return mergeTypicaHandlingRates(offerList.typicaHandlingRates, rates);
        }));
    }, [simulatedOrderPrice, offers, offerList]);
    var typicaHandlingFeeCalculation = React.useMemo(function () {
        if (itemType === 'TypicaStockItem')
            return {
                type: 'weight',
                rates: TypicaStockHandlingRates,
            };
        if (Object.keys(offerList.typicaHandlingRates).includes('less_than_5_bags')) {
            return {
                type: 'bagAmount',
                rates: typicaHandlingRatesAverage,
            };
        }
        else {
            return {
                type: 'weight',
                rates: typicaHandlingRatesAverage,
            };
        }
    }, [itemType, offerList.typicaHandlingRates, typicaHandlingRatesAverage]);
    var typicaHandlingFeePercentage = calcHandingFee(typicaHandlingFeeCalculation, simulatedOrderPrice.ordersTotalQuantity, parseFloat(simulatedOrderPrice.ordersTotalWeight));
    return (React.createElement(React.Fragment, null,
        React.createElement(Alert, { severity: "success", sx: {
                mt: {
                    xs: 0,
                    md: 3,
                },
                mb: 3,
            } },
            React.createElement(AlertTitle, null, t('pages.roasterOrder.typicaHandlingFee', {
                percentage: typicaHandlingFeePercentage,
            })),
            React.createElement(TypicaHandlingRateExplanation, { typicaHandlingRates: typicaHandlingRatesAverage, isTypicaStockOrder: itemType === 'TypicaStockItem' })),
        React.createElement(Card, null,
            totalBagAmount > 0 && (React.createElement(OrderPriceTable, { orderPriceItemType: itemType, offers: offers, orderPrice: orderPrice, renderDiscountFields: renderDiscountFields, isUSRoaster: isUSRoaster })),
            React.createElement(Divider, null),
            React.createElement(Stack, { direction: "column", spacing: 0.5, sx: {
                    padding: {
                        xs: '16px 16px 16px 48px',
                        md: '16px 16px 16px 56px',
                    },
                } },
                React.createElement(Box, { sx: {
                        textAlign: 'right',
                        typography: function (theme) { return theme.customTypography.subtitle2; },
                    } }, t('pages.roasterOrder.totalFee', {
                    fee: formatMoneyAmount(orderPrice.total, simulatedOrderPrice.currency),
                })),
                typicaHandlingFeeCalculation.type === 'bagAmount' ? (React.createElement(React.Fragment, null,
                    React.createElement(Box, { sx: {
                            textAlign: 'right',
                            typography: function (theme) { return theme.customTypography.body3; },
                        } }, t('pages.roasterOrder.totalBags', {
                        amount: simulatedOrderPrice.ordersTotalQuantity,
                    })),
                    React.createElement(Stack, { direction: "column", alignItems: "flex-end", spacing: 0.5 },
                        orderedTotalBagAmount != 0 && (React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.body3; } } }, t('pages.roasterOrder.totalOrderedBags', {
                            amount: orderedTotalBagAmount,
                        }))),
                        orderedOneTimeUpfrontTotalBagAmount != 0 &&
                            simulatedOrderPrice.paymentTiming === 'one_time_upfront' && (React.createElement(Box, { sx: {
                                typography: function (theme) { return theme.customTypography.body3; },
                            } },
                            "(",
                            t('pages.roasterOrder.totalOneTimeUpfrontOrderedBags', {
                                amount: orderedOneTimeUpfrontTotalBagAmount,
                            }),
                            ")")),
                        orderedTotalWeight != 0 && (React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.body3; } } }, t('pages.roasterOrder.totalOrderedWeight', {
                            weight: orderedTotalWeight,
                        })))))) : (React.createElement(React.Fragment, null,
                    React.createElement(Box, { sx: {
                            textAlign: 'right',
                            typography: function (theme) { return theme.customTypography.body3; },
                        } }, t('pages.roasterOrder.totalWeight', {
                        weight: parseFloat(simulatedOrderPrice.ordersTotalWeight),
                    })),
                    React.createElement(Stack, { direction: "column", alignItems: "flex-end", spacing: 0.5 }, orderedTotalWeight != 0 && (React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.body3; } } }, t('pages.roasterOrder.totalOrderedWeight', {
                        weight: orderedTotalWeight,
                    }))))))))));
};
export default SimulatedOrderPrice;
var mergeTypicaHandlingRates = function (offerListTypicaHandlingRates, offerTypicaHandlingRates) {
    if (Object.keys(offerListTypicaHandlingRates).includes('less_than_5_bags')) {
        var rate1_1 = offerListTypicaHandlingRates;
        var rate2_1 = offerTypicaHandlingRates;
        return {
            less_than_5_bags: rate2_1['less_than_5_bags'] || rate1_1['less_than_5_bags'],
            more_than_5_bags: rate2_1['more_than_5_bags'] || rate1_1['more_than_5_bags'],
            more_than_10_bags: rate2_1['more_than_10_bags'] || rate1_1['more_than_10_bags'],
            more_than_50_bags: rate2_1['more_than_50_bags'] || rate1_1['more_than_50_bags'],
        };
    }
    var rate1 = offerListTypicaHandlingRates;
    var rate2 = offerTypicaHandlingRates;
    return {
        less_than_300kg: rate2['less_than_300kg'] || rate1['less_than_300kg'],
        more_than_300kg: rate2['more_than_300kg'] || rate1['more_than_300kg'],
        more_than_600kg: rate2['more_than_600kg'] || rate1['more_than_600kg'],
        more_than_3000kg: rate2['more_than_3000kg'] || rate1['more_than_3000kg'],
    };
};
var calculateAverageRate = function (typicaHandlingRates) {
    if (Object.keys(typicaHandlingRates[0]).includes('less_than_5_bags')) {
        var rates_1 = typicaHandlingRates;
        var sumRates_1 = rates_1.reduce(function (result, rate) {
            return {
                less_than_5_bags: result['less_than_5_bags'] + parseFloat(rate['less_than_5_bags']),
                more_than_5_bags: result['more_than_5_bags'] + parseFloat(rate['more_than_5_bags']),
                more_than_10_bags: result['more_than_10_bags'] + parseFloat(rate['more_than_10_bags']),
                more_than_50_bags: result['more_than_50_bags'] + parseFloat(rate['more_than_50_bags']),
            };
        }, {
            less_than_5_bags: 0,
            more_than_5_bags: 0,
            more_than_10_bags: 0,
            more_than_50_bags: 0,
        });
        return {
            less_than_5_bags: (sumRates_1['less_than_5_bags'] / rates_1.length).toFixed(2),
            more_than_5_bags: (sumRates_1['more_than_5_bags'] / rates_1.length).toFixed(2),
            more_than_10_bags: (sumRates_1['more_than_10_bags'] / rates_1.length).toFixed(2),
            more_than_50_bags: (sumRates_1['more_than_50_bags'] / rates_1.length).toFixed(2),
        };
    }
    var rates = typicaHandlingRates;
    var sumRates = rates.reduce(function (result, rate) {
        return {
            less_than_300kg: result['less_than_300kg'] + parseFloat(rate['less_than_300kg']),
            more_than_300kg: result['more_than_300kg'] + parseFloat(rate['more_than_300kg']),
            more_than_600kg: result['more_than_600kg'] + parseFloat(rate['more_than_600kg']),
            more_than_3000kg: result['more_than_3000kg'] + parseFloat(rate['more_than_3000kg']),
        };
    }, {
        less_than_300kg: 0,
        more_than_300kg: 0,
        more_than_600kg: 0,
        more_than_3000kg: 0,
    });
    return {
        less_than_300kg: (sumRates['less_than_300kg'] / rates.length).toFixed(2),
        more_than_300kg: (sumRates['more_than_300kg'] / rates.length).toFixed(2),
        more_than_600kg: (sumRates['more_than_600kg'] / rates.length).toFixed(2),
        more_than_3000kg: (sumRates['more_than_3000kg'] / rates.length).toFixed(2),
    };
};
var templateObject_1;
